import React, { useState, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { updatePrivacySettings } from '../../services/settingsService'; // ✅ Import from service
import ToggleSwitch from './ui/ToggleSwitch';
import ErrorModal from "../Modal/ErrorModal";

const AccountPrivacySection = () => {
  const { user, apiCall, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  // Load account privacy setting from user profile
  useEffect(() => {   
    if (user) {
      if (user.profile?.privacySettings) {
        const settings = user.profile.privacySettings;
        
        // Set private account setting - convert number to boolean for ToggleSwitch
        setIsPrivate(settings.isPrivateAccount === 1);
      }
      
      setIsLoading(false);
    }
  }, [user]);

  // Function to save account privacy setting
  const saveAccountPrivacy = async (value) => {
    setIsSaving(true);

    try {
      const dbValue = value ? 1 : 0;
      const result = await updatePrivacySettings(apiCall, 'isPrivateAccount', dbValue);

      if (result.success) {
        if (refreshUser) {
          await refreshUser();
        }
        return { success: true };
      }

      return {
        success: false,
        error: result.error || 'Failed to update privacy setting.',
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update privacy setting.',
      };
    } finally {
      setIsSaving(false);
    }
  };

  // Handler function for private account toggle
  const handlePrivateAccountChange = async (value) => {
    const previousValue = isPrivate;
    setIsPrivate(value); // Optimistic update

    const saveResult = await saveAccountPrivacy(value);

    if (!saveResult.success) {
      setIsPrivate(previousValue); // Revert
      setErrorModal({ isOpen: true, message: saveResult.error });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white p-4">
        <h3 className="font-semibold text-black mb-4">Account Privacy</h3>
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Lock className="w-5 h-5 text-gray-600" />
            <div>
              <p className="font-medium text-black">Private Account</p>
              <p className="text-xs text-gray-600">Only approved followers can see your posts</p>
            </div>
          </div>
          <ToggleSwitch 
            checked={isPrivate} 
            onChange={handlePrivateAccountChange}
            disabled={isSaving}
          />
        </div>

        <div className="bg-white rounded-lg p-3 border border-gray-400">
          <p className="text-sm text-gray-600 leading-relaxed">
            {isPrivate ? (
              <>
                <strong>Your Wryven account is currently private.</strong> Only approved followers can see your posts, followers, following list, and admirers list. 
                New followers will need to send a follow request that you can approve or deny.
              </>
            ) : (
              <>
                <strong>Your Wryven account is currently public.</strong> Anyone can see your posts, followers, following list, and admirers list. 
                You can switch to a private account to control who can follow you and see your content.
              </>
            )}
          </p>
          <p className="text-xs text-gray-600 mt-2">
            Your profile picture, name, bio, and username are always visible to everyone on Wryven.
          </p>
        </div>
      </div>
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />
    </div>
  );
};

export default AccountPrivacySection;